import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Bell,
  BellDot,
  Check,
  CheckCircle2,
  ShieldCheck,
  ShieldAlert,
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
  Clock,
} from "lucide-react";

const ICONS = {
  "kyc.approved": ShieldCheck,
  "kyc.rejected": ShieldAlert,
  "kyc.submitted": ShieldCheck,
  "withdrawal.approved": ArrowUpFromLine,
  "withdrawal.denied": ShieldAlert,
  "withdrawal.pending": Clock,
  "transfer.received": ArrowDownToLine,
  "transfer.sent": Send,
  "deposit.completed": ArrowDownToLine,
  "security.login": ShieldCheck,
  "security.password_reset": ShieldCheck,
  info: Bell,
};

const ACCENTS = {
  "kyc.approved": "text-[#7BBF8E]",
  "kyc.rejected": "text-[#E27A6A]",
  "withdrawal.approved": "text-[#7BBF8E]",
  "withdrawal.denied": "text-[#E27A6A]",
  "withdrawal.pending": "text-[#B58957]",
  "transfer.received": "text-[#7BBF8E]",
  "transfer.sent": "text-[#B58957]",
  "deposit.completed": "text-[#7BBF8E]",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {
      // silent
    }
  };

  const poll = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(poll, 45_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleItemClick = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((prev) =>
          prev.map((p) => (p.id === n.id ? { ...p, read: true } : p))
        );
        setUnread((u) => Math.max(0, u - 1));
      } catch {}
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((p) => ({ ...p, read: true })));
      setUnread(0);
    } catch {}
  };

  const Icon = unread > 0 ? BellDot : Bell;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg text-white/80 hover:bg-white/[0.06] hover:text-white transition-colors"
        aria-label="Notifications"
        data-testid="notif-bell"
      >
        <Icon className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-[#B58957] text-[#0A130E]"
            data-testid="notif-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-[#0F1B14] border border-white/10 rounded-xl shadow-2xl z-40 overflow-hidden"
          data-testid="notif-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm font-semibold text-white">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-[#B58957] hover:underline flex items-center gap-1"
                data-testid="notif-mark-all"
              >
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-white/50 text-sm">
                You're all caught up.
              </div>
            ) : (
              items.map((n) => {
                const Ico = ICONS[n.kind] || Bell;
                const color = ACCENTS[n.kind] || "text-white/70";
                return (
                  <button
                    key={n.id}
                    onClick={() => handleItemClick(n)}
                    data-testid={`notif-item-${n.id}`}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${
                      !n.read ? "bg-white/[0.02]" : ""
                    }`}
                  >
                    <Ico className={`w-4 h-4 mt-0.5 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">
                        {n.title}
                        {!n.read && (
                          <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[#B58957] align-middle" />
                        )}
                      </div>
                      {n.body && (
                        <div className="text-xs text-white/60 mt-0.5 line-clamp-2">
                          {n.body}
                        </div>
                      )}
                      <div className="text-[10px] text-white/40 mt-1">
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <Link
            to="/app/transactions"
            onClick={() => setOpen(false)}
            className="block px-4 py-3 text-center text-xs text-white/60 hover:text-white hover:bg-white/[0.04] border-t border-white/10"
          >
            View activity →
          </Link>
        </div>
      )}
    </div>
  );
}
